import {z} from "zod";

export type RaceResultStatus = 'FINISHED' | 'DNS' | 'DNF' | 'DSQ';

export type ParsedRaceTimeResult = {
  status: RaceResultStatus;
  time?: number;
  disqualification?: string;
};

export const RACE_STATUS_MAPPINGS: Record<string, RaceResultStatus> = {
  'dns': 'DNS',
  'n.a.': 'DNS',
  'na': 'DNS',
  'nicht angetreten': 'DNS',
  'dnf': 'DNF',
  'ausgeschieden': 'DNF',
  'dsq': 'DSQ',
  'disqualifiziert': 'DSQ',
};

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('de-AT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

export const formatDateTime = (dateString: string) => {
  const date = new Date(dateString);
  return {
    date: date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }),
    time: date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    })
  };
};

export const dateStringSchema = z.string().refine((val) => {
  const date = new Date(val);
  return !isNaN(date.getTime());
}, "Must be a valid date string")

/**
 * Formats race time (in seconds) to MM:SS.HH format
 * @param time - Time in seconds (e.g., 123.45 for 2:03.45)
 * @returns Formatted time string or '-' if no time provided
 */
export const formatRaceTime = (time?: number | undefined | null) => {
  if (!time) return '-';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  const hundredths = Math.floor((time % 1) * 100);
  return `${minutes}:${seconds.toString().padStart(2, '0')}.${hundredths.toString().padStart(2, '0')}`;
};

/**
 * Parses a race time string into seconds
 * @param timeStr - Time string in MM:SS.HH format
 * @returns Time in seconds or undefined if invalid format
 */
export const parseRaceTime = (timeStr: string): number | undefined => {
  const match = timeStr.match(/^(\d+):(\d{2})\.(\d{2})$/);
  if (!match) return undefined;

  const [, minutes, seconds, hundredths] = match;
  return parseInt(minutes) * 60 + parseInt(seconds) + parseInt(hundredths) / 100;
};

export function formatName(name: string): { firstName: string; lastName: string } {
  // Handle "LASTNAME, Firstname" format
  const parts = name.split(',').map(part => part.trim());
  const lastName = parts[0];
  const upperCaseCount = lastName.split('').filter(char => char === char.toUpperCase()).length;

  if (parts.length === 2) {
    return {
      lastName: upperCaseCount > lastName.length / 2 ? lastName.toUpperCase() : lastName,
      firstName: parts[1]
    };
  }

  // Fallback if no comma found
  return {
    firstName: name,
    lastName: ''
  };
}

export function parseRaceTimeResult(input: string): ParsedRaceTimeResult {
  if (!input) return { status: 'DNS' };

  const normalized = input.trim().toLowerCase();
  const status = RACE_STATUS_MAPPINGS[normalized];

  if (status) return {
    status,
    disqualification: status === 'DSQ' ? input : undefined
  };

  try {
    let timeInSeconds: number;
    // Handle German format with comma
    const cleanedInput = normalized.replace(',', '.');

    if (cleanedInput.includes(':')) {
      const [minutes, seconds] = cleanedInput.split(':');
      timeInSeconds = (parseInt(minutes, 10) * 60) + parseFloat(seconds);
    } else {
      timeInSeconds = parseFloat(cleanedInput);
    }

    if (!isNaN(timeInSeconds)) {
      return { status: 'FINISHED', time: timeInSeconds };
    }
  } catch {
    return {
      status: 'DSQ',
      disqualification: input
    };
  }

  return {
    status: 'DSQ',
    disqualification: input
  };
}
